import React from "react";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Info, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export interface UnconfiguredAccount {
  id: string;
  ad_account_id: string;
  ad_account_name: string;
  currency: string | null;
  timezone: string | null;
}

interface AccountSelectionStepProps {
  accounts: UnconfiguredAccount[];
  selectedAccountId: string | null;
  onSelect: (account: UnconfiguredAccount) => void;
}

export function AccountSelectionStep({
  accounts,
  selectedAccountId,
  onSelect,
}: AccountSelectionStepProps) {
  const [search, setSearch] = useState("");
  const showSearch = accounts.length > 6;

  const filtered = useMemo(() => {
    if (!search.trim()) return accounts;
    const q = search.toLowerCase();
    return accounts.filter(
      (a) =>
        a.ad_account_name.toLowerCase().includes(q) ||
        a.ad_account_id.toLowerCase().includes(q)
    );
  }, [accounts, search]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          Choose your ad account
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Select the account you'd like to configure first.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Select the account you'd like to set up first. You can configure your
          other accounts anytime from Settings.
        </AlertDescription>
      </Alert>

      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {filtered.map((account) => {
          const isSelected = selectedAccountId === account.ad_account_id;
          return (
            <Card
              key={account.id}
              onClick={() => onSelect(account)}
              className={cn(
                "cursor-pointer transition-all hover:border-primary/50",
                isSelected && "ring-2 ring-primary border-primary"
              )}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {account.ad_account_name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {account.ad_account_id}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  {account.currency && (
                    <Badge variant="secondary" className="text-xs">
                      {account.currency}
                    </Badge>
                  )}
                  {account.timezone && (
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {account.timezone}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No accounts match your search.
          </p>
        )}
      </div>
    </div>
  );
}
